import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Tooltip,
  OverlayTrigger,
  Alert,
} from "react-bootstrap";
import Select, { components as SelectComponents } from "react-select";
import TextLoading from "../global/TextLoading";
import ModalSuccess from "../global/modals/ModalSuccess";
import {
  UpdateStaffAdditionalFields,
  getOrganizationStaffAdditionalFields,
} from "../../api/Staff";

function FieldTypeOption({ innerProps, data, selectProps, ...rest }) {
  return (
    <SelectComponents.Option
      {...rest}
      innerProps={{
        ...innerProps,
        id: `selectFieldType${selectProps.fieldIndex}-option-${data.value}`,
      }}
      data={data}
      selectProps={selectProps}
    />
  );
}
FieldTypeOption.propTypes = {
  innerProps: PropTypes.object.isRequired,
  data: PropTypes.shape({ value: PropTypes.string }).isRequired,
  selectProps: PropTypes.shape({ fieldIndex: PropTypes.number }).isRequired,
};

const stylesSelect = {
  dropdownIndicator: (provided) => ({
    ...provided,
    svg: { fill: "#595e63" },
  }),
  option: (provided, state) => ({
    ...provided,
    color: "black",
    backgroundColor: state.isSelected ? "#ffd9e0" : "white",
    "&:hover": { backgroundColor: "#fff0f3" },
  }),
  control: (baseStyles) => ({ ...baseStyles, borderColor: "#ced4da" }),
};

const structureOption = { name: "", value: "", isInvalid: false, active: 1 };
const structureCheck = {
  name: "",
  value: 0,
  isInvalid: false,
  active: 1,
  checked: 0,
};

export default function AdditionalFieldsModalStaff({ show, onHide }) {
  const [t] = useTranslation(["staff", "global"]);

  const optionsFieldTypes = [
    {
      value: "short_answer",
      label: t("staff:ModalAdditionalFields.shortAnswer"),
      icon: "bi bi-filter-left",
    },
    {
      value: "drop_down",
      label: t("staff:ModalAdditionalFields.dropDownList"),
      icon: "bi bi-caret-down-square",
    },
    {
      value: "multiple_option",
      label: t("staff:ModalAdditionalFields.multipleChoice"),
      icon: "bi bi-ui-radios",
    },
    {
      value: "check",
      label: t("staff:ModalAdditionalFields.checkBoxes"),
      icon: "bi bi-ui-checks",
    },
  ];

  const structureFieldType = {
    name: "",
    type: { ...optionsFieldTypes[0] },
    required: 0,
    isInvalid: false,
  };

  const [listAdditionalFields, setListAdditionalFields] = useState([
    { ...structureFieldType },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorAlert, setErrorAlert] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deletionAlert, setDeletionAlert] = useState(false);

  useEffect(() => {
    getAdditionalFields();
  }, []);

  const getAdditionalFields = () => {
    getOrganizationStaffAdditionalFields({
      additional_fields_sections_ids: [3],
    })
      .then((response) => {
        if (response.data?.[0]?.fields?.length > 0) {
          const fields = response.data[0].fields.map((field) => ({
            id: field.additional_fields_id,
            name: field.name,
            type: optionsFieldTypes.find((type) => type.value === field.type),
            required: field.required,
            only_admins_can_fill: field.only_admins_can_fill,
            options:
              field.options?.map((option) => ({
                name: option.name,
                value: option.value,
                active: option.active,
                checked: option.checked,
              })) ?? null,
          }));
          setListAdditionalFields(fields);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const skeletonFields = () =>
    [0, 1].map((i) => (
      <Row
        key={i}
        className="mb-3 p-3"
        style={{ border: "solid 1.5px #dbdddd", borderRadius: "8px" }}
      >
        <Col md={6}>
          <span
            className="skeleton mb-1"
            style={{ height: "14px", width: "60px", borderRadius: "4px" }}
          />
          <span
            className="skeleton"
            style={{ height: "38px", borderRadius: "6px" }}
          />
        </Col>
        <Col md={5}>
          <span
            className="skeleton mb-1"
            style={{ height: "14px", width: "80px", borderRadius: "4px" }}
          />
          <span
            className="skeleton"
            style={{ height: "38px", borderRadius: "6px" }}
          />
        </Col>
        <Col md={1} style={{ paddingTop: "42px" }}>
          <span
            className="skeleton"
            style={{ height: "20px", width: "20px", borderRadius: "50%" }}
          />
        </Col>
      </Row>
    ));

  const TooltipWarningType = (
    <Tooltip id="button-tooltip">
      {t("staff:ModalAdditionalFields.typeChangeAlertMessage")}
    </Tooltip>
  );

  const addNewField = () => {
    setListAdditionalFields([
      { ...structureFieldType },
      ...listAdditionalFields,
    ]);
  };

  const removeField = (index) => {
    const isExisting = !!listAdditionalFields[index].id;
    setListAdditionalFields(listAdditionalFields.filter((_, i) => i !== index));
    if (isExisting) setDeletionAlert(true);
  };

  const changeTitleField = (e, index) => {
    const newList = listAdditionalFields.map((item, i) => {
      if (i === index) {
        const isDuplicate =
          e.target.value !== "" &&
          listAdditionalFields
            .filter((_, j) => j !== index)
            .map((f) => f.name.toLowerCase())
            .includes(e.target.value.toLowerCase());
        return { ...item, name: e.target.value, isInvalid: isDuplicate };
      }
      return item;
    });
    setListAdditionalFields(newList);
  };

  const changeFieldType = (e, index) => {
    const newList = [...listAdditionalFields].map((item, i) => {
      if (i === index) {
        item = { ...item, type: e };
        switch (e.value) {
          case "short_answer":
            delete item.options;
            break;
          case "drop_down":
          case "multiple_option":
            item.options = [{ ...structureOption }, { ...structureOption }];
            break;
          case "check":
            item.options = [{ ...structureCheck }];
            break;
          default:
            break;
        }
      }
      return item;
    });
    setListAdditionalFields(newList);
  };

  const addNewOption = (fieldPosition) => () => {
    const newList = listAdditionalFields.map((item, i) => {
      if (i === fieldPosition) {
        const newOption =
          item.type.value === "check"
            ? { ...structureCheck }
            : { ...structureOption };
        return { ...item, options: [...item.options, newOption] };
      }
      return item;
    });
    setListAdditionalFields(newList);
  };

  const removeOption = (fieldPosition, optionPosition) => {
    const newList = [...listAdditionalFields].map((item, i) => {
      if (i === fieldPosition) {
        const options = item.options.map((opt, j) =>
          j === optionPosition ? { ...opt, active: 0 } : opt,
        );
        return { ...item, options };
      }
      return item;
    });
    setListAdditionalFields(newList);
  };

  const changeValueOption = (e, fieldPosition, optionPosition) => {
    const newList = [...listAdditionalFields].map((item, i) => {
      if (i === fieldPosition) {
        const options = item.options.map((option, j) => {
          if (j === optionPosition) {
            const activeOptions = item.options.filter((o) => o.active === 1);
            const isInvalid =
              e.target.value !== "" &&
              activeOptions
                .map((o) => o.name.toLowerCase())
                .includes(e.target.value.toLowerCase());
            return { ...option, name: e.target.value, isInvalid };
          }
          return option;
        });
        return { ...item, options };
      }
      return item;
    });
    setListAdditionalFields(newList);
  };

  const validateFields = () => {
    if (listAdditionalFields.length === 0) return false;
    if (listAdditionalFields.some((f) => f.name === "" || f.isInvalid))
      return false;
    const typesWithOptions = ["drop_down", "multiple_option", "check"];
    for (const field of listAdditionalFields) {
      if (typesWithOptions.includes(field.type.value)) {
        for (const option of field.options) {
          if (option.name === "" && option.active === 1) return false;
          if (option.isInvalid) return false;
        }
      }
    }
    return true;
  };

  const saveAdditionalFields = (confirmSave = "") => {
    if (saving) return;
    if (deletionAlert && confirmSave !== "confirm") {
      setShowConfirmDelete(true);
      return;
    }
    setDeletionAlert(false);
    setSaving(true);

    const fields = listAdditionalFields.map((item) => {
      let options = null;
      if (item.type.value !== "short_answer") {
        options = item.options.map((option, index) => {
          if (item.type.value === "check") {
            return {
              name: option.name,
              value: option.value || index + 1,
              active: option.active,
              checked: option.checked,
            };
          }
          return {
            name: option.name,
            value: option.value || index + 1,
            active: option.active,
          };
        });
      }
      return {
        additional_fields_id: item.id,
        name: item.name,
        type: item.type.value,
        required: item.required,
        options,
      };
    });

    const payload = { additional_fields_sections_id: 3, fields };

    UpdateStaffAdditionalFields(payload)
      .then((response) => {
        if (response.description) {
          setShowConfirmDelete(false);
          setErrorAlert(response.description);
          setTimeout(() => setErrorAlert(null), 5000);
          return;
        }
        onHide();
      })
      .catch(() => {})
      .finally(() => {
        setSaving(false);
      });
  };

  const evaluateShowRemoveOption = (listOptions, option) => {
    const countActive = listOptions.filter((o) => o.active === 1).length;
    return (
      countActive <= 2 || (option.active !== undefined && option.active === 0)
    );
  };

  const evaluateShowRemoveCheckOption = (listOptions, option) => {
    const countActive = listOptions.filter((o) => o.active === 1).length;
    return (
      countActive <= 1 || (option.active !== undefined && option.active === 0)
    );
  };

  const selectTypes = (field, index) => (
    <Form.Group className="mb-3 mt-3" controlId={"fieldType" + index}>
      <Form.Label className="mb-0">
        {t("staff:ModalAdditionalFields.fieldType")}
      </Form.Label>
      <Select
        inputId={"selectFieldType" + index}
        fieldIndex={index}
        options={optionsFieldTypes}
        isSearchable={false}
        components={{ IndicatorSeparator: () => null, Option: FieldTypeOption }}
        value={field.type}
        styles={stylesSelect}
        formatOptionLabel={(data) => (
          <>
            <i className={data.icon} />{" "}
            <span style={{ fontSize: "1rem", fontWeight: "400" }}>
              {data.label}
            </span>
          </>
        )}
        onChange={(e) => changeFieldType(e, index)}
      />
    </Form.Group>
  );

  const optionsForDropDownAndMultipleOption = (field, index) => {
    let count = 0;
    return field.options.map((option, indexOption) => {
      if (option.active === 1) count++;
      return (
        <React.Fragment key={indexOption}>
          <Col
            md={evaluateShowRemoveOption(field.options, option) ? 12 : 11}
            hidden={option.active !== undefined && option.active === 0}
          >
            <Form.Group
              className="mb-3"
              controlId={"optionTitle" + index + "_" + indexOption}
            >
              <Form.Label className="mb-0">
                {t("staff:ModalAdditionalFields.option") + " " + count}
              </Form.Label>
              <Form.Control
                id={"inputOption" + index + "_" + indexOption}
                type="text"
                placeholder={
                  t("staff:ModalAdditionalFields.option") + " " + count
                }
                value={option.name}
                onChange={(e) => changeValueOption(e, index, indexOption)}
                maxLength={30}
                isInvalid={option.isInvalid}
              />
              {option.isInvalid && (
                <Form.Text style={{ color: "#ea2c54" }}>
                  {t("staff:ModalAdditionalFields.fieldNameAlreadyExists")}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col
            md={1}
            hidden={evaluateShowRemoveOption(field.options, option)}
            className="fs-6"
            style={{ paddingTop: "25px", color: "#5e5e5e" }}
          >
            <i
              id={"btnRemoveOption" + index + "_" + indexOption}
              className="bi bi-dash-circle-fill align-middle"
              role="button"
              style={{ marginLeft: "17px" }}
              onClick={() => removeOption(index, indexOption)}
            />
          </Col>
        </React.Fragment>
      );
    });
  };

  const optionsForCheck = (field, index) => {
    let count = 0;
    return field.options.map((option, indexOption) => {
      if (option.active === 1) count++;
      return (
        <React.Fragment key={indexOption}>
          <Col
            md={evaluateShowRemoveCheckOption(field.options, option) ? 12 : 11}
            hidden={option.active !== undefined && option.active === 0}
          >
            <Form.Group
              className="mb-3"
              controlId={"checkOption" + index + "_" + indexOption}
            >
              <Form.Label className="mb-0">
                {t("staff:ModalAdditionalFields.option") + " " + count}
              </Form.Label>
              <Form.Control
                id={"inputCheckOption" + index + "_" + indexOption}
                type="text"
                placeholder={
                  t("staff:ModalAdditionalFields.option") + " " + count
                }
                value={option.name}
                onChange={(e) => changeValueOption(e, index, indexOption)}
                maxLength={30}
                isInvalid={option.isInvalid}
              />
              {option.isInvalid && (
                <Form.Text style={{ color: "#ea2c54" }}>
                  {t("staff:ModalAdditionalFields.fieldNameAlreadyExists")}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col
            md={1}
            hidden={evaluateShowRemoveCheckOption(field.options, option)}
            className="fs-6"
            style={{ paddingTop: "25px", color: "#5e5e5e" }}
          >
            <i
              id={"btnRemoveCheckOption" + index + "_" + indexOption}
              className="bi bi-dash-circle-fill align-middle"
              role="button"
              style={{ marginLeft: "17px" }}
              onClick={() => removeOption(index, indexOption)}
            />
          </Col>
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <Modal
        id="modalAdditionalFieldsStaff"
        show={show && !showConfirmDelete}
        size="lg"
        aria-labelledby="contained-modal-title-lg"
      >
        <Modal.Header className="modal-header">
          <Modal.Title className="modal-title text-center">
            {t("staff:ModalAdditionalFields.title")}
          </Modal.Title>
          <Button
            id="btnAddFieldAdditionalFieldsStaff"
            variant="outline-secondary"
            className="end-0 fw-bold"
            onClick={addNewField}
            disabled={loading}
          >
            <i className="bi bi-plus-lg" />
            {t("staff:ModalAdditionalFields.addField")}
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>
              {t("staff:ModalAdditionalFields.selectSection")}
            </Form.Label>
            <Form.Select
              id="sectionStaff"
              value="staff_basic_info"
              disabled
              onChange={() => {}}
            >
              <option value="staff_basic_info">
                {t("staff:ModalAdditionalFields.staff_basic_info")}
              </option>
            </Form.Select>
          </Form.Group>
          <div className="mb-4">
            {t("staff:ModalAdditionalFields.addFieldMessage")}
          </div>
          <Container
            style={{
              minHeight: "255px",
              maxHeight: window.innerHeight / 1.5 + "px",
              overflow: "auto",
            }}
          >
            {loading
              ? skeletonFields()
              : listAdditionalFields.map((field, index) => (
                  <Row
                    key={index}
                    className="mb-3"
                    style={{
                      border: "solid 1.5px #dbdddd",
                      borderRadius: "8px",
                    }}
                  >
                    <Col md={6}>
                      <Form.Group
                        className="mb-3 mt-3"
                        controlId={"fieldTitle" + index}
                      >
                        <Form.Label className="mb-0">
                          {t("staff:ModalAdditionalFields.fieldTitle")}
                        </Form.Label>
                        <Form.Control
                          id={"inputFieldTitle" + index}
                          type="text"
                          placeholder={t(
                            "staff:ModalAdditionalFields.fieldTitle",
                          )}
                          value={field.name}
                          onChange={(e) => changeTitleField(e, index)}
                          maxLength={30}
                          isInvalid={field.isInvalid}
                        />
                        {field.isInvalid && (
                          <Form.Text style={{ color: "#ea2c54" }}>
                            {t(
                              "staff:ModalAdditionalFields.fieldNameAlreadyExists",
                            )}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      {field.id ? (
                        <OverlayTrigger
                          trigger="focus"
                          placement="right"
                          overlay={TooltipWarningType}
                        >
                          {selectTypes(field, index)}
                        </OverlayTrigger>
                      ) : (
                        selectTypes(field, index)
                      )}
                    </Col>
                    <Col
                      md={1}
                      className="fs-6 float-end"
                      style={{ paddingTop: "42px", color: "#5e5e5e" }}
                    >
                      <i
                        id={"btnRemoveField" + index}
                        className="bi bi-dash-circle-fill align-middle"
                        role="button"
                        style={{ marginLeft: "17px" }}
                        onClick={() => removeField(index)}
                      />
                    </Col>

                    {(field.type?.value === "drop_down" ||
                      field.type?.value === "multiple_option") &&
                      optionsForDropDownAndMultipleOption(field, index)}

                    {field.type?.value === "check" &&
                      optionsForCheck(field, index)}

                    {(field.type?.value === "drop_down" ||
                      field.type?.value === "multiple_option" ||
                      field.type?.value === "check") && (
                      <Col md={12} className="text-danger">
                        <div
                          id={"btnAddOption" + index}
                          onClick={addNewOption(index)}
                          role="button"
                          className="d-inline-block"
                        >
                          {t("staff:ModalAdditionalFields.addOption")}
                        </div>{" "}
                        <i
                          onClick={addNewOption(index)}
                          role="button"
                          className="d-inline-block bi bi-plus-circle-fill fs-6 align-middle"
                        />
                      </Col>
                    )}

                    <Col md={12}>
                      <div className="float-end">
                        <div className="d-inline-block me-1 align-top">
                          {t("staff:ModalAdditionalFields.required")}
                        </div>
                        <div className="form-check form-switch d-inline-block">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={"isRequired" + index}
                            onChange={(e) => {
                              const newList = listAdditionalFields.map(
                                (item, i) => {
                                  if (i === index)
                                    return {
                                      ...item,
                                      required: e.target.checked ? 1 : 0,
                                    };
                                  return item;
                                },
                              );
                              setListAdditionalFields(newList);
                            }}
                            checked={field.required === 1}
                            defaultChecked={false}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                ))}
          </Container>

          {errorAlert && (
            <Alert
              id="errorAlertAdditionalFieldsStaff"
              className="mt-3"
              variant="warning"
            >
              {errorAlert}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="btnCancelAdditionalFieldsStaff"
            variant="outline-secondary"
            onClick={onHide}
            disabled={saving}
          >
            {t("global:buttons.cancel")}
          </Button>
          <Button
            id="btnSaveAdditionalFieldsStaff"
            variant="primary"
            onClick={saveAdditionalFields}
            disabled={!validateFields() || saving}
          >
            {saving ? (
              <TextLoading text={t("global:buttons.saving")} variant="light" />
            ) : (
              t("global:buttons.save")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {showConfirmDelete && (
        <ModalSuccess
          id="modalConfirmDeleteFieldsStaff"
          acceptButtonId="btnConfirmDeleteFieldsStaff"
          showModalSuccess={showConfirmDelete}
          title={t("staff:ModalAdditionalFields.editFields")}
          message={t("staff:ModalAdditionalFields.editFieldsMessage")}
          fnAcceptButton={() => saveAdditionalFields("confirm")}
          fnCancelButton={() => setShowConfirmDelete(false)}
          loading={saving}
        />
      )}
    </>
  );
}

AdditionalFieldsModalStaff.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};
